import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { EditDialog } from 'common/components/Dialog/EditDialog';
import { Actions, Subjects } from 'permissions/ability';
import { useAbility } from 'permissions/hooks/useAbility';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { TagsAutoCompleteInput } from 'src/tags/Components/Inputs/TagsAutocompleteInput';
import { useState } from 'react';
import { useTagsAutoCompleteApi } from '@/tags/Components/Inputs/hooks/useTagsAutoCompleteApi';
import { tagsMutationKeys } from 'src/tags/utils/queries';
import { CARD_TYPE } from 'src/tags/models/schemas';
import { Tag } from 'src/tags/models/tags';
import { RSimInventoryItem } from '../constants/rsim';

export type RSimEditDialogContentProps = {
  open: boolean;
  data: RSimInventoryItem;
  onClose: () => void;
  onTagsChange: (data: { cardId: string; tags: Tag[] }) => void;
};

export const RSimEditDialog = ({
  open,
  data,
  onClose,
  onTagsChange,
}: RSimEditDialogContentProps) => {
  const queryClient = useQueryClient();
  const [tagsDirty, setTagsDirty] = useState(false);
  const euicc = data.euicc;

  const ability = useAbility();

  const [tagsAutocompleteRef, { saveTags, resetTags }] = useTagsAutoCompleteApi();
  const isTagsMutating = useIsMutating({ mutationKey: tagsMutationKeys.assignTags });

  if (!open) {
    return null;
  }

  return (
    <EditDialog
      open={open}
      subtitle={`rSIM ${euicc}`}
      unsavedChanges={tagsDirty}
      onApplyChangesClick={async () => {
        if (ability.can(Actions.edit, Subjects.tags)) {
          await saveTags();
          queryClient.removeQueries({ queryKey: [CARD_TYPE.RSIM, 'tags', 'infinity'] });
        }
        onClose();
      }}
      onCancelClick={onClose}
      onClearChangesClick={async () => {
        resetTags();
      }}
      isDisabled={!!isTagsMutating}
    >
      <Stack
        direction="row"
        gap={48}
        alignItems="center"
        sx={{ borderLeft: (theme) => `1px solid ${theme.palette.text.tertiary}` }}
      >
        <SimInventoryCan I={Actions.edit} a={Subjects.tags}>
          <Typography
            variant="text2"
            color="text.secondary"
            sx={{
              ml: 16,
            }}
          >
            Tags
          </Typography>
          <Box flex={1}>
            <TagsAutoCompleteInput
              tags={data.tags?.items || []}
              ref={tagsAutocompleteRef}
              onTagsChange={onTagsChange}
              id={euicc}
              onTagsDirtyChange={setTagsDirty}
            />
          </Box>
        </SimInventoryCan>
      </Stack>
    </EditDialog>
  );
};
